/** @jsxImportSource @gasman/smush */

import './pattern_table.css';
import { Component, fields } from '@gasman/smush';
var removeChildren = require('remove-children');

import { NOTE_NAMES_BY_VALUE, NoteField } from '../../common/notes';

const NOTE_INPUT_KEY_MAPPINGS = {
    'key:KeyZ': { setNote: 0, triggerNote: true },
    'key:KeyS': { setNote: 1, triggerNote: true },
    'key:KeyX': { setNote: 2, triggerNote: true },
    'key:KeyD': { setNote: 3, triggerNote: true },
    'key:KeyC': { setNote: 4, triggerNote: true },
    'key:KeyV': { setNote: 5, triggerNote: true },
    'key:KeyG': { setNote: 6, triggerNote: true },
    'key:KeyB': { setNote: 7, triggerNote: true },
    'key:KeyH': { setNote: 8, triggerNote: true },
    'key:KeyN': { setNote: 9, triggerNote: true },
    'key:KeyJ': { setNote: 10, triggerNote: true },
    'key:KeyM': { setNote: 11, triggerNote: true },
    'val:Backspace': { setNote: null, triggerNote: true },
    'val:Enter': { setNote: null, triggerNote: true },
    'val:r': { setRest: true, triggerNote: true },
};

const HEX_INPUT_KEY_MAPPINGS = {
    'val:0': { setHexDigit: 0, triggerNote: true },
    'val:1': { setHexDigit: 1, triggerNote: true },
    'val:2': { setHexDigit: 2, triggerNote: true },
    'val:3': { setHexDigit: 3, triggerNote: true },
    'val:4': { setHexDigit: 4, triggerNote: true },
    'val:5': { setHexDigit: 5, triggerNote: true },
    'val:6': { setHexDigit: 6, triggerNote: true },
    'val:7': { setHexDigit: 7, triggerNote: true },
    'val:8': { setHexDigit: 8, triggerNote: true },
    'val:9': { setHexDigit: 9, triggerNote: true },
    'val:a': { setHexDigit: 10, triggerNote: true },
    'val:b': { setHexDigit: 11, triggerNote: true },
    'val:c': { setHexDigit: 12, triggerNote: true },
    'val:d': { setHexDigit: 13, triggerNote: true },
    'val:e': { setHexDigit: 14, triggerNote: true },
    'val:f': { setHexDigit: 15, triggerNote: true },
};

class InputAdapter {
    constructor(fieldName) {
        this.field = fieldName;
    }
}

class NoteInputAdapter extends InputAdapter {
    keyMappings = NOTE_INPUT_KEY_MAPPINGS;
    className = 'note';

    constructor(fieldName, linkedSampleField) {
        super(fieldName);
        this.linkedSampleField = linkedSampleField || null;
    }

    renderField(elem, val) {
        elem.innerText = NOTE_NAMES_BY_VALUE[val];
    }

    runAction(
        action,
        pattern,
        channelIndex,
        rowIndex,
        editorState,
        undoBuffer,
        undoPath,
    ) {
        var noteVal;
        if (action.setRest) {
            noteVal = 255;
        } else if (action.setNote === null) {
            noteVal = 0;
        } else {
            noteVal = 1 + 12 * editorState.octave + action.setNote;
        }
        let forwardCommandData = { [this.field]: noteVal };
        let reverseCommandData = {
            [this.field]: pattern.getField(channelIndex, rowIndex, this.field),
        };
        pattern.setField(channelIndex, rowIndex, this.field, noteVal);
        if (editorState.sampleNumber !== 0 && this.linkedSampleField !== null) {
            forwardCommandData[this.linkedSampleField] =
                editorState.sampleNumber;
            reverseCommandData[this.linkedSampleField] = pattern.getField(
                channelIndex,
                rowIndex,
                this.linkedSampleField,
            );
            pattern.setField(
                channelIndex,
                rowIndex,
                this.linkedSampleField,
                editorState.sampleNumber,
            );
        }
        if (undoBuffer) {
            undoBuffer.push({
                path: [...undoPath, channelIndex, rowIndex],
                forward: forwardCommandData,
                reverse: reverseCommandData,
            });
        }
    }
}

class HexInputAdapter extends InputAdapter {
    keyMappings = HEX_INPUT_KEY_MAPPINGS;

    renderField(elem, val) {
        elem.innerText = val.toString(16).toUpperCase();
    }

    runAction(
        action,
        pattern,
        channelIndex,
        rowIndex,
        editorState,
        undoBuffer,
        undoPath,
    ) {
        let lastValue = pattern.getField(channelIndex, rowIndex, this.field);
        pattern.setField(
            channelIndex,
            rowIndex,
            this.field,
            action.setHexDigit,
        );
        if (undoBuffer) {
            undoBuffer.push({
                path: [...undoPath, channelIndex, rowIndex],
                forward: { [this.field]: action.setHexDigit },
                reverse: { [this.field]: lastValue },
            });
        }
    }
}

class HexHighDigitAdapter extends InputAdapter {
    keyMappings = HEX_INPUT_KEY_MAPPINGS;

    renderField(elem, val) {
        elem.innerText = (val >> 4).toString(16).toUpperCase();
    }

    runAction(
        action,
        pattern,
        channelIndex,
        rowIndex,
        editorState,
        undoBuffer,
        undoPath,
    ) {
        let lastValue = pattern.getField(channelIndex, rowIndex, this.field);
        var val = pattern.getField(channelIndex, rowIndex, this.field);
        val = (action.setHexDigit << 4) | (val & 0x0f);
        pattern.setField(channelIndex, rowIndex, this.field, val);
        if (undoBuffer) {
            undoBuffer.push({
                path: [...undoPath, channelIndex, rowIndex],
                forward: { [this.field]: val },
                reverse: { [this.field]: lastValue },
            });
        }
    }
}

class HexLowDigitAdapter extends InputAdapter {
    keyMappings = HEX_INPUT_KEY_MAPPINGS;

    renderField(elem, val) {
        elem.innerText = (val & 0x0f).toString(16).toUpperCase();
    }

    runAction(
        action,
        pattern,
        channelIndex,
        rowIndex,
        editorState,
        undoBuffer,
        undoPath,
    ) {
        let lastValue = pattern.getField(channelIndex, rowIndex, this.field);
        var val = pattern.getField(channelIndex, rowIndex, this.field);
        val = (val & 0xf0) | action.setHexDigit;
        pattern.setField(channelIndex, rowIndex, this.field, val);
        if (undoBuffer) {
            undoBuffer.push({
                path: [...undoPath, channelIndex, rowIndex],
                forward: { [this.field]: val },
                reverse: { [this.field]: lastValue },
            });
        }
    }
}

class PatternTable extends Component {
    GLOBAL_KEY_MAPPINGS = {
        /* key mappings active on all field types */
        'val:ArrowRight': { keyDown: ['moveCursorRight'] },
        'val:ArrowLeft': { keyDown: ['moveCursorLeft'] },
        'val:ArrowUp': { keyDown: ['moveCursorUp'] },
        'val:ArrowDown': { keyDown: ['moveCursorDown'] },
        'val:ArrowRight+shift': { keyDown: ['moveCursorRightAndSelect'] },
        'val:ArrowLeft+shift': { keyDown: ['moveCursorLeftAndSelect'] },
        'val:ArrowUp+shift': { keyDown: ['moveCursorUpAndSelect'] },
        'val:ArrowDown+shift': { keyDown: ['moveCursorDownAndSelect'] },
        'key:Digit1+shift': { keyDown: ['setOctave', 1] },
        'key:Digit2+shift': { keyDown: ['setOctave', 2] },
        'key:Digit3+shift': { keyDown: ['setOctave', 3] },
        'key:Digit4+shift': { keyDown: ['setOctave', 4] },
        'key:Digit5+shift': { keyDown: ['setOctave', 5] },
        'key:Digit6+shift': { keyDown: ['setOctave', 6] },
        'key:Digit7+shift': { keyDown: ['setOctave', 7] },
        'key:Digit8+shift': { keyDown: ['setOctave', 8] },
        'val: ': { keyDown: ['playOrStop'] },
        'key:KeyC+meta': { keyDown: ['copy'] },
        'key:KeyC+ctrl': { keyDown: ['copy'] },
        'key:KeyV+meta': { keyDown: ['paste'] },
        'key:KeyV+ctrl': { keyDown: ['paste'] },
        'key:KeyA+meta': { keyDown: ['selectAll'] },
        'key:KeyA+ctrl': { keyDown: ['selectAll'] },
    };

    TYPED_KEY_MAPPINGS = {
        note: NOTE_INPUT_KEY_MAPPINGS,
        hexDigit: HEX_INPUT_KEY_MAPPINGS,
    };

    constructor(app, patternModel) {
        super();
        this.editorState = null;
        this.app = app;

        // input adapters define the logic for rendering and handling keypress actions
        // for an individual input field.
        // this.inputAdapters is a list of lists, one list per channel, each one defining the
        // sequence of input fields for that channel
        this.inputAdapters = [];
        this.channelFieldNames = [];
        // patternModel.fields.channels = a TupleField of channel fields
        patternModel.fields.channels.fieldList.forEach((channelField, chan) => {
            // each channelField is a ListField representing a list of rows, with the subfield
            // (representing a row) being a StructField of the component fields of a row
            var rowFields = channelField.subfield.fieldList;
            var adapters = [];
            var fieldNames = [];
            rowFields.forEach((field) => {
                fieldNames.push(field.name);
                if (field instanceof NoteField) {
                    adapters.push(
                        new NoteInputAdapter(
                            field.name,
                            field.linkedSampleField,
                        ),
                    );
                } else if (field instanceof fields.IntegerField) {
                    if (field.max <= 15) {
                        adapters.push(new HexInputAdapter(field.name));
                    } else {
                        adapters.push(new HexHighDigitAdapter(field.name));
                        adapters.push(new HexLowDigitAdapter(field.name));
                    }
                } else {
                    throw 'unknown field type: ' + field.type;
                }
            });
            this.inputAdapters[chan] = adapters;
            this.channelFieldNames[chan] = fieldNames;
        });
        this.channelCount = this.inputAdapters.length;

        this.playbackRow = null;

        this.pattern = null;
        this.undoBuffer = null;
        this.undoPath = null;
        this.rows = [];

        this.table = <table class="pattern-table"></table>;

        this.pendingKeyUpEvents = {};
        /* if this is non-null, it indicates that we are currently auditioning
        a note in response to a keydown event, and contains the keycode of the
        pending keyup event that will cause us to release the note
         */
        this.releaseNoteOnKey = null;

        this.selectStartChannel = null;
        this.selectEndChannel = null;
        this.selectStartRow = null;
        this.selectEndRow = null;
        this.dragStartChannel = null;
        this.dragStartRow = null;

        this.table.onkeydown = (e) => {
            var receiver;
            var rowIndex, channelIndex, inputIndex;

            if (this.pendingKeyUpEvents[e.code]) {
                // ignore key repeats on keypresses that have pending keyup events
                return false;
            }

            /* Look up by both physical key code (e.code) and key value (e.key). This allows us to map
            actions to both the physical key position (e.g. for entering notes in piano keyboard
            layout) and its semantics (e.g. entering hex digits), which do not necessarily correspond
            to each other (see: AZERTY and Dvorak keyboard layouts)
            */
            var keyCode = 'key:' + e.code;
            var keyVal = 'val:' + e.key;
            if (e.metaKey) {
                keyCode += '+meta';
                keyVal += '+meta';
            }
            if (e.ctrlKey) {
                keyCode += '+ctrl';
                keyVal += '+ctrl';
            }
            if (e.shiftKey) {
                keyCode += '+shift';
                keyVal += '+shift';
            }

            [rowIndex, channelIndex, inputIndex] = this.getCursorPosition();

            /* Look for a global key binding */
            var action =
                this.GLOBAL_KEY_MAPPINGS[keyCode] ||
                this.GLOBAL_KEY_MAPPINGS[keyVal];
            if (action) {
                // global actions are handled by applying action.keydown as a method on this object
                receiver = this;
            } else {
                // look up key mappings for the active input
                var inputAdapter = this.inputAdapters[channelIndex][inputIndex];
                var keyMappings = inputAdapter.keyMappings;
                action = keyMappings[keyCode] || keyMappings[keyVal];
                if (action) {
                    receiver = inputAdapter;
                }
            }

            if (action) {
                /* if a note-triggering key is currently down, do not
                accept any non-note-triggering keypresses (such as moving to
                another field) until it's released */
                if (!action.triggerNote && this.releaseNoteOnKey !== null) {
                    return false;
                }

                receiver.runAction(
                    action,
                    this.pattern,
                    channelIndex,
                    rowIndex,
                    this.editorState,
                    this.undoBuffer,
                    this.undoPath,
                );
                if (action.triggerNote) {
                    if (this.releaseNoteOnKey !== null) {
                        /* release note before retriggering it */
                        this.releaseNote();
                    }
                    this.app.player.auditionRow(
                        this.editorState.patternNumber,
                        rowIndex,
                    );
                    this.pendingKeyUpEvents[e.code] = action;
                    this.releaseNoteOnKey = e.code;
                }
                return false;
            }
        };

        document.body.addEventListener('keyup', (e) => {
            if (this.releaseNoteOnKey == e.code && e.code !== null) {
                this.releaseNote();
                this.releaseNoteOnKey = null;
            }
            var action = this.pendingKeyUpEvents[e.code];
            if (action) {
                delete this.pendingKeyUpEvents[e.code];
                return false;
            }
        });

        this.app.player.on('playRow', (patternNumber, rowIndex) => {
            if (patternNumber == this.editorState.patternNumber) {
                this.setPlaybackRow(rowIndex);
            } else {
                this.setPlaybackRow(null);
            }
        });
        this.app.player.on('stopPlaying', () => {
            this.setPlaybackRow(null);
        });

        this.mouseIsDown = false;
        this.table.addEventListener('mousedown', (event) => {
            // only trigger on left mouse button
            if (event.button != 0) return;

            this.mouseIsDown = true;
            this.selectNone();
            var path = this._getPathToElement(event.target);
            if (path && path.length >= 3) {
                var channelIndex = parseInt(path[2].dataset.channelIndex);
                var rowIndex = parseInt(path[1].dataset.rowIndex);

                if (!isNaN(channelIndex) && !isNaN(rowIndex)) {
                    this.dragStartChannel = channelIndex;
                    this.dragStartRow = rowIndex;
                }
            }
        });
        this.table.addEventListener('dblclick', (event) => {
            var path = this._getPathToElement(event.target);
            if (path && path.length >= 3) {
                var channelIndex = parseInt(path[2].dataset.channelIndex);
                var rowIndex = parseInt(path[1].dataset.rowIndex);

                if (!isNaN(channelIndex) && !isNaN(rowIndex)) {
                    this.select(channelIndex, rowIndex, channelIndex, rowIndex);
                }
            }
        });
        this.table.addEventListener('mousemove', (event) => {
            if (this.mouseIsDown && this.dragStartChannel !== null) {
                var path = this._getPathToElement(event.target);
                if (path && path.length >= 3) {
                    var channelIndex = parseInt(path[2].dataset.channelIndex);
                    var rowIndex = parseInt(path[1].dataset.rowIndex);

                    if (!isNaN(channelIndex) && !isNaN(rowIndex)) {
                        this.select(
                            this.dragStartChannel,
                            this.dragStartRow,
                            channelIndex,
                            rowIndex,
                        );
                    }
                    event.target.focus();
                }
                //event.preventDefault();
                //return false;
            }
        });
    }

    render(container) {
        container.appendChild(this.table);
    }

    show() {
        document.body.addEventListener('mouseup', this.onMouseUp);
    }

    hide() {
        document.body.removeEventListener('mouseup', this.onMouseUp);
    }

    onMouseUp = (event) => {
        // only trigger on left mouse button
        if (event.button != 0) return;

        this.mouseIsDown = false;
        this.dragStartChannel = null;
        this.dragStartRow = null;
    };

    runAction(action) {
        var func, args;
        [func, ...args] = action.keyDown;
        this[func].apply(this, args);
    }

    bindPattern(pattern, opts) {
        opts = opts || {};

        if (this.pattern == pattern && this.undoBuffer == opts.undoBuffer) {
            return;
        }

        this.unbindPattern();
        this.pattern = pattern;

        if (opts.undoBuffer) {
            this.undoBuffer = opts.undoBuffer;
            this.undoPath = [...opts.undoPath, 'channels'];
        }

        for (var rowIndex = 0; rowIndex < this.pattern.length; rowIndex++) {
            this.appendRow(rowIndex);
        }

        this.pattern.on('changeField', this.onChangeField);
        this.pattern.on('changeLength', this.onChangeLength);
    }

    unbindPattern() {
        if (this.pattern) {
            this.pattern.removeListener('changeField', this.onChangeField);
            this.pattern.removeListener('changeLength', this.onChangeLength);
            this.pattern = null;
            this.undoBuffer = null;
            this.undoPath = null;
            removeChildren(this.table);
            this.rows = [];
            this.playbackRow = null;
        }
    }

    bindState(state) {
        this.editorState = state;
    }
    unbindState() {
        this.editorState = null;
    }

    appendRow(rowIndex) {
        var row = (
            <tr data-row-index={rowIndex}>
                <th>{rowIndex.toString()}</th>
            </tr>
        );

        this.rows[rowIndex] = {
            tr: row,
            cells: [],
        };

        for (var chan = 0; chan < this.channelCount; chan++) {
            this.createCell(row, rowIndex, chan);
        }
        this.table.appendChild(row);
    }

    createCell(row, rowIndex, chan) {
        var td = <td data-channel-index={chan}></td>;

        var cell = { td: td, inputs: [] };
        this.rows[rowIndex].cells[chan] = cell;

        this.inputAdapters[chan].forEach((inputAdapter, i) => {
            var input = (
                <span
                    class={inputAdapter.className}
                    tabindex="0"
                    data-input-index={i}
                ></span>
            );

            td.appendChild(input);
            cell.inputs[i] = input;
            inputAdapter.renderField(
                input,
                this.pattern.getField(chan, rowIndex, inputAdapter.field),
            );
        });
        row.appendChild(td);
    }

    onChangeField = (chan, row, fieldName, val) => {
        var cell = this.rows[row].cells[chan];

        this.inputAdapters[chan].forEach((input, i) => {
            if (input.field == fieldName) {
                input.renderField(cell.inputs[i], val);
            }
        });
    };

    onChangeLength = (newLength) => {
        var oldLength = this.rows.length;
        if (newLength < oldLength) {
            for (var i = newLength; i < oldLength; i++) {
                this.table.removeChild(this.rows[i].tr);
            }
            this.rows.splice(newLength);
        } else if (newLength > oldLength) {
            for (i = oldLength; i < newLength; i++) {
                this.appendRow(i);
            }
        }
    };

    _getPathToElement(element) {
        /* Return a list of element's ancestors, starting from this.table and ending at element,
        or null if element is not a child of this.table */
        if (!this.table.contains(element)) return null;
        var path = [element];
        while (element != this.table) {
            element = element.parentNode;
            path.unshift(element);
        }
        return path;
    }

    getCursorPosition() {
        var path = this._getPathToElement(document.activeElement);
        if (!path || path.length < 4) return null;
        var field = path[3];
        var cell = path[2];
        var row = path[1];

        var inputIndex = parseInt(field.dataset.inputIndex);
        var channelIndex = parseInt(cell.dataset.channelIndex);
        var rowIndex = parseInt(row.dataset.rowIndex);

        return [rowIndex, channelIndex, inputIndex];
    }

    setCursorPosition(rowIndex, channelIndex, inputIndex) {
        this.rows[rowIndex].cells[channelIndex].inputs[inputIndex].focus();
    }

    selectCellAtCursor() {
        var rowIndex, channelIndex;
        [rowIndex, channelIndex] = this.getCursorPosition();
        this.select(channelIndex, rowIndex, channelIndex, rowIndex);
    }
    extendSelectionToCursor() {
        var rowIndex, channelIndex;
        [rowIndex, channelIndex] = this.getCursorPosition();
        this.select(
            this.selectStartChannel,
            this.selectStartRow,
            channelIndex,
            rowIndex,
        );
    }

    moveCursorRight(keepSelection = false) {
        var rowIndex, channelIndex, inputIndex;
        [rowIndex, channelIndex, inputIndex] = this.getCursorPosition();
        inputIndex += 1;
        if (inputIndex >= this.inputAdapters[channelIndex].length) {
            inputIndex = 0;
            channelIndex += 1;
            if (channelIndex >= this.channelCount) channelIndex = 0;
        }

        if (!keepSelection) this.selectNone();
        this.setCursorPosition(rowIndex, channelIndex, inputIndex);
    }

    moveCursorRightAndSelect() {
        if (this.selectStartChannel === null) {
            this.selectCellAtCursor();
        }
        this.moveCursorRight(true);
        this.extendSelectionToCursor();
    }

    moveCursorLeft(keepSelection = false) {
        var rowIndex, channelIndex, inputIndex;
        [rowIndex, channelIndex, inputIndex] = this.getCursorPosition();
        inputIndex -= 1;
        if (inputIndex < 0) {
            channelIndex -= 1;
            if (channelIndex < 0) channelIndex = this.channelCount - 1;
            inputIndex = this.inputAdapters[channelIndex].length - 1;
        }

        if (!keepSelection) this.selectNone();
        this.setCursorPosition(rowIndex, channelIndex, inputIndex);
    }

    moveCursorLeftAndSelect() {
        if (this.selectStartChannel === null) {
            this.selectCellAtCursor();
        }
        this.moveCursorLeft(true);
        this.extendSelectionToCursor();
    }

    moveCursorUp(keepSelection = false) {
        var rowIndex, channelIndex, inputIndex;
        [rowIndex, channelIndex, inputIndex] = this.getCursorPosition();
        rowIndex -= 1;
        if (rowIndex < 0) rowIndex = this.pattern.length - 1;
        if (!keepSelection) this.selectNone();
        this.setCursorPosition(rowIndex, channelIndex, inputIndex);
    }

    moveCursorUpAndSelect() {
        if (this.selectStartChannel === null) {
            this.selectCellAtCursor();
        }
        this.moveCursorUp(true);
        this.extendSelectionToCursor();
    }

    moveCursorDown(keepSelection = false) {
        var rowIndex, channelIndex, inputIndex;
        [rowIndex, channelIndex, inputIndex] = this.getCursorPosition();
        rowIndex = (rowIndex + 1) % this.pattern.length;
        if (!keepSelection) this.selectNone();
        this.setCursorPosition(rowIndex, channelIndex, inputIndex);
    }

    moveCursorDownAndSelect() {
        if (this.selectStartChannel === null) {
            this.selectCellAtCursor();
        }
        this.moveCursorDown(true);
        this.extendSelectionToCursor();
    }

    releaseNote() {
        this.app.player.stop();
    }

    setOctave(octave) {
        this.editorState.octave = octave;
    }

    setPlaybackRow(newRow) {
        if (this.playbackRow !== null) {
            if (this.playbackRow === newRow) return;
            this.rows[this.playbackRow].tr.classList.remove('playback-row');
        }
        this.playbackRow = newRow;
        if (newRow !== null) {
            let row = this.rows[newRow].tr;
            row.classList.add('playback-row');
            if (this.editorState.isFollowingPlayback) {
                row.scrollIntoView({ block: 'center' });
            }
        }
    }

    playOrStop() {
        if (this.app.player.isPlaying) {
            this.app.player.stop();
        } else if (!this.app.player.isActive) {
            this.app.player.playPattern(this.editorState.patternNumber);
        }
    }

    focus() {
        this.setCursorPosition(0, 0, 0);
    }

    selectNone() {
        if (this.selectStartChannel !== null) {
            for (
                var chan = this.selectStartChannel;
                chan < this.selectEndChannel;
                chan++
            ) {
                for (
                    var row = this.selectStartRow;
                    row < this.selectEndRow;
                    row++
                ) {
                    this.rows[row].cells[chan].td.classList.remove('selected');
                }
            }
            this.selectStartChannel =
                this.selectEndChannel =
                this.selectStartRow =
                this.selectEndRow =
                    null;
        }
    }

    select(x0, y0, x1, y1) {
        var startChannel = Math.min(x0, x1);
        var endChannel = Math.max(x0, x1) + 1;
        var startRow = Math.min(y0, y1);
        var endRow = Math.max(y0, y1) + 1;
        var chan, row;

        if (this.selectStartChannel !== null) {
            /* deselect existing selected items from channels left of startChannel */
            for (chan = this.selectStartChannel; chan < startChannel; chan++) {
                for (
                    row = this.selectStartRow;
                    row < this.selectEndRow;
                    row++
                ) {
                    this.rows[row].cells[chan].td.classList.remove('selected');
                }
            }
            /* deselect existing selected items from channels right of endChannel */
            for (chan = endChannel; chan < this.selectEndChannel; chan++) {
                for (
                    row = this.selectStartRow;
                    row < this.selectEndRow;
                    row++
                ) {
                    this.rows[row].cells[chan].td.classList.remove('selected');
                }
            }
            /* deselect existing selected items from rows above startRow */
            for (row = this.selectStartRow; row < startRow; row++) {
                for (chan = startChannel; chan < endChannel; chan++) {
                    this.rows[row].cells[chan].td.classList.remove('selected');
                }
            }
            /* deselect existing selected items from rows below endRow */
            for (row = endRow; row < this.selectEndRow; row++) {
                for (chan = startChannel; chan < endChannel; chan++) {
                    this.rows[row].cells[chan].td.classList.remove('selected');
                }
            }
            /* select items in channels left of previous startChannel */
            for (chan = startChannel; chan < this.selectStartChannel; chan++) {
                for (row = startRow; row < endRow; row++) {
                    this.rows[row].cells[chan].td.classList.add('selected');
                }
            }
            /* select items in channels right of previous endChannel */
            for (chan = this.selectEndChannel; chan < endChannel; chan++) {
                for (row = startRow; row < endRow; row++) {
                    this.rows[row].cells[chan].td.classList.add('selected');
                }
            }
            /* select items in rows above previous startRow */
            for (row = startRow; row < this.selectStartRow; row++) {
                for (chan = startChannel; chan < endChannel; chan++) {
                    this.rows[row].cells[chan].td.classList.add('selected');
                }
            }
            /* select items in rows below previous endRow */
            for (row = this.selectEndRow; row < endRow; row++) {
                for (chan = startChannel; chan < endChannel; chan++) {
                    this.rows[row].cells[chan].td.classList.add('selected');
                }
            }
        } else {
            /* nothing selected already; just fill everything in range */
            for (chan = startChannel; chan < endChannel; chan++) {
                for (row = startRow; row < endRow; row++) {
                    this.rows[row].cells[chan].td.classList.add('selected');
                }
            }
        }

        this.selectStartChannel = startChannel;
        this.selectEndChannel = endChannel;
        this.selectStartRow = startRow;
        this.selectEndRow = endRow;
    }

    selectAll() {
        this.select(0, 0, this.channelCount - 1, this.pattern.length - 1);
    }

    copy() {
        if (this.selectStartChannel === null) return;
        let patternData = [];
        for (
            let chan = this.selectStartChannel;
            chan < this.selectEndChannel;
            chan++
        ) {
            let fieldNames = this.channelFieldNames[chan];

            let channelData = [];
            for (
                let row = this.selectStartRow;
                row < this.selectEndRow;
                row++
            ) {
                let rowData = fieldNames.map((name) => {
                    return this.pattern.getField(chan, row, name);
                });
                channelData.push(rowData);
            }
            patternData.push({
                fields: fieldNames,
                data: channelData,
            });
        }
        this.app.clipboard = {
            type: 'pattern',
            data: patternData,
        };
    }
    paste() {
        if (!(this.app.clipboard && this.app.clipboard.type == 'pattern'))
            return;

        let rowIndex, channelIndex;
        [rowIndex, channelIndex] = this.getCursorPosition();

        /* check channel data types match */
        for (var i = 0; i < this.app.clipboard.data.length; i++) {
            if (channelIndex + i >= this.channelCount) break;
            let expectedFields = this.app.clipboard.data[i].fields;
            let actualFields = this.channelFieldNames[channelIndex + i];
            if (expectedFields.length != actualFields.length) return;
            for (var j = 0; j < expectedFields.length; j++) {
                if (expectedFields[j] != actualFields[j]) return;
            }
        }

        /* all OK, so proceed to copy data */

        /* set up entry for undo buffer */
        let command = {
            path: this.undoPath,
            forward: {},
            reverse: {},
        };

        for (i = 0; i < this.app.clipboard.data.length; i++) {
            if (channelIndex + i >= this.channelCount) break;

            let chanForwardCommandData = (command.forward[channelIndex + i] =
                {});
            let chanReverseCommandData = (command.reverse[channelIndex + i] =
                {});

            let chanData = this.app.clipboard.data[i].data;
            let fields = this.channelFieldNames[channelIndex + i];

            for (j = 0; j < chanData.length; j++) {
                let rowForwardCommandData = (chanForwardCommandData[
                    rowIndex + j
                ] = {});
                let rowReverseCommandData = (chanReverseCommandData[
                    rowIndex + j
                ] = {});

                fields.forEach((field, k) => {
                    let oldVal = this.pattern.getField(
                        channelIndex + i,
                        rowIndex + j,
                        field,
                    );
                    let newVal = chanData[j][k];

                    this.pattern.setField(
                        channelIndex + i,
                        rowIndex + j,
                        field,
                        newVal,
                    );
                    rowForwardCommandData[field] = newVal;
                    rowReverseCommandData[field] = oldVal;
                });
            }
        }

        if (this.undoBuffer) {
            this.undoBuffer.push(command);
        }
    }
}

export { PatternTable };
